<template>
  <div class="container" v-if="!isLoading">
    <div class="row">
      <div class="col-12">
        <TopNav class="mt-4" />
        <img class="mt-3" :srcset="`${logo} 3x`" />
        <p class="mt-4">
          {{ $t('SETTINGS.TEXT.CONNECT_YOUR_GOOGLE_CALENDAR') }}
        </p>
        <HMActivatedTag
          v-if="isGoogleCalendarConnected"
          :label="$t('SETTINGS.LABEL.CONNECTED')"
          class="my-3"
        />
      </div>
      <div class="col-12">
        <hr class="my-2" />
        <div
          class="d-flex flex-row justify-content-between align-items-center mt-4"
        >
          <h3>{{ $t('SETTINGS.LABEL.CONNECTED_ACCOUNTS') }}</h3>
          <label class="cursor-pointer" @click="showConnectModal">
            {{ $t('SETTINGS.BUTTON.CONNECT_ACCOUNT')
            }}<i class="fal fa-plus c-coral ml-2"></i>
          </label>
        </div>
        <HMTable
          class="mt-3"
          :items="googleCalendarConnections"
          :fields="googleCalendarConnectionFields"
        >
          <template slot="name" slot-scope="{ data }">
            <span>{{ getConnectionName(data) }}</span>
          </template>
          <template slot="is_default" slot-scope="{ data }">
            <HMCheck v-model="data.default" />
          </template>
          <template slot="connected" slot-scope="{ data }">
            <span>{{ $formatDate(data.created) }}</span>
          </template>
          <template slot="edit" slot-scope="{ data }">
            <HMIconButton
              :icon="['far', 'ellipsis-v']"
              :id="`popover-${data.id}`"
            />
            <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
              <HMPopoverMenuItem
                v-if="!data.default"
                @click="setDefaultConnection(data.id)"
              >
                {{ $t('SETTINGS.LABEL.MAKE_DEFAULT') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem @click="showUpdateConnectionNameModal(data)">
                {{ $t('SETTINGS.LABEL.RENAME') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem
                v-if="canRemoveConnection(data)"
                v-confirm="{
                  title: $t(
                    'SETTINGS.TITLE.DISCONNECT_GOOGLE_CALENDAR_CONNECTION'
                  ),
                  text: $t(
                    'SETTINGS.TEXT.DISCONNECT_GOOGLE_CALEDAR_CONNECTION_DESCRIPTION'
                  ),
                  confirmButton: $t(
                    'SETTINGS.BUTTON.DISCONNECT_FROM_GOOGLE_CALENDAR'
                  ),
                  confirm: () => removeConnection(data.id),
                }"
              >
                <span class="d-flex justify-content-between align-items-center">
                  {{ $t('SETTINGS.LABEL.DISCONNECT') }}
                  <i class="far fa-trash-alt c-coral ml-4"></i>
                </span>
              </HMPopoverMenuItem>
            </HMPopover>
          </template>
        </HMTable>
      </div>
      <div class="col-12 small mt-4">
        <div
          class="p-4 rounded bg-lightest-gray"
          v-html="$t('SETTINGS.TEXT.GOOGLE_API_DISCLAIMER')"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import TopNav from '@/web/components/navigation/TopNav.vue';
import { getConfig } from '@/web/utils/configuration';
import { mapGetters } from 'vuex';
import UpgradeYourPlanModal from '../../../components/settings/UpgradeYourPlanModal';
import { replaceRedirectURLWithCurrentDomain } from '../../../utils/url';
import { errorToMessage } from '../../../../core/utils/errors';
import { HMRenameModal } from '../../../components/common';
import { sanitize } from '@/core/utils/utils';
import ConnectGoogleModal from '../../../components/settings/ConnectGoogleModal';

export default Vue.extend({
  name: 'GoogleCalendarIntegration',
  components: {
    TopNav,
  },
  computed: {
    ...mapGetters({
      teacher: 'user/teacher',
    }),
    applicationName() {
      return getConfig().applicationName;
    },
    isGoogleCalendarConnected() {
      return this.googleCalendarConnections?.length > 0;
    },
    googleCalendarConnectionFields() {
      return [
        {
          name: 'name',
          title: this.$t('SETTINGS.TABLE.NAME'),
        },
        {
          name: 'is_default',
          title: this.$t('SETTINGS.TABLE.DEFAULT'),
        },
        {
          name: 'connected',
          title: this.$t('SETTINGS.TABLE.CONNECTED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    upgradePlanDescription() {
      return this.$tc(
        'SETTINGS.TEXT.YOUR_PLAN_SUPPORTS_UP_TO_X_GOOGLE_CALENDAR_CONNECTIONS_VARIANT',
        this.teacher.max_google_calendar_connections,
        {
          connectionsLimit: this.teacher.max_google_calendar_connections,
        }
      );
    },
  },
  data() {
    return {
      logo: '/assets/images/integrations/google-calendar.png',
      isLoading: true,
      inProgress: false,
      googleCalendarConnections: null,
      isInit: false,
      isSignIn: false,
    };
  },
  methods: {
    sanitize,
    canRemoveConnection(connection) {
      if (connection.default && this.googleCalendarConnections?.length > 1) {
        return false;
      }

      return true;
    },
    getConnectionName(googleCalendarConnection) {
      if (googleCalendarConnection.email) {
        return `${googleCalendarConnection.name} (${googleCalendarConnection.email})`;
      }
      return googleCalendarConnection.name;
    },
    showConnectModal() {
      this.$modal.show(ConnectGoogleModal, {
        title: this.$t('SETTINGS.TITLE.CONNECT_GOOGLE_CALENDAR'),
        description: this.$t(
          'SETTINGS.TEXT.CONNECT_GOOGLE_CALENDAR_DESCRIPTION'
        ),
        connectFunction: this.connect,
      });
    },
    async connect() {
      if (
        this.googleCalendarConnections?.length >=
        this.teacher.max_google_calendar_connections
      ) {
        this.$modal.show(UpgradeYourPlanModal, {
          title: 'SETTINGS.TEXT.UPGRADE_YOUR_PLAN',
          description: this.upgradePlanDescription,
        });
        return;
      }
      const redirectURL = replaceRedirectURLWithCurrentDomain(
        process.env.VUE_APP_GOOGLE_CALENDAR_REDIRECT_URL
      );
      const rootUrl = `https://accounts.google.com/o/oauth2/v2/auth`;

      const options = {
        redirect_uri: redirectURL,
        client_id: process.env.VUE_APP_GOOGLE_CALENDAR_CLIENT_ID,
        access_type: 'offline',
        response_type: 'code',
        prompt: 'consent',
        scope: [
          'https://www.googleapis.com/auth/calendar.events',
          'https://www.googleapis.com/auth/calendar.readonly',
          'https://www.googleapis.com/auth/userinfo.email',
          'https://www.googleapis.com/auth/userinfo.profile',
        ].join(' '),
      };

      const qs = new URLSearchParams(options);

      window.location = `${rootUrl}?${qs.toString()}`;
    },
    checkForOAuthCode() {
      const code = this.$route.query.code;
      const scope = this.$route.query.scope;
      if (!code) return;
      const query = { ...this.$route.query };
      delete query.code;
      delete query.scope;
      this.$router.replace({ query });

      this.addGoogleCalendarConnection(code, scope);
    },
    async addGoogleCalendarConnection(code, scope) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/addGoogleCalendarConnection', {
          code,
          scope,
        });
        this.googleCalendarConnections = await this.$store.dispatch(
          'setting/getGoogleCalendarConnections'
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_CALENDAR_SUCCESSFULLY_CONNECTED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async setDefaultConnection(connectionId) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(
          'setting/setGoogleCalendarDefaultConnection',
          connectionId
        );
        this.googleCalendarConnections = await this.$store.dispatch(
          'setting/getGoogleCalendarConnections'
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_CALENDAR_SUCCESSFULLY_UPDATED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    showUpdateConnectionNameModal(conection) {
      this.$modal.show(
        HMRenameModal,
        {
          value: conection.name,
        },
        {},
        {
          renamed: async name => {
            await this.updateConnectionName(conection.id, name);
          },
        }
      );
    },
    async updateConnectionName(connectionId, name) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/setGoogleCalendarConnectionName', {
          connectionId,
          name,
        });
        this.googleCalendarConnections = await this.$store.dispatch(
          'setting/getGoogleCalendarConnections'
        );
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_CALENDAR_SUCCESSFULLY_UPDATED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async removeConnection(conectionId) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(
          'setting/removeGoogleCalendarConnection',
          conectionId
        );
        this.googleCalendarConnections = await this.$store.dispatch(
          'setting/getGoogleCalendarConnections'
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.GOOGLE_CALENDAR_SUCCESSFULLY_REMOVED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    this.$store.dispatch('user/loadUserInfo');
    this.googleCalendarConnections = await this.$store.dispatch(
      'setting/getGoogleCalendarConnections'
    );
    this.checkForOAuthCode();
    this.isLoading = false;
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.cursor-pointer {
  cursor: pointer;
}
</style>
